import { defer, finalize, from, Observable, switchMap } from 'rxjs';

/**
 * Runs observables one at a time per key: work for key X starts only after the
 * previous work for X has completed, errored or been unsubscribed. Different
 * keys do not wait for each other. Errors propagate to the caller unchanged.
 */
export class KeyedSerializer<K = string> {
    private readonly tails = new Map<K, Promise<void>>();

    serialize<T>(key: K, work: () => Observable<T>): Observable<T> {
        return defer(() => {
            const previous = this.tails.get(key);
            let release!: () => void;
            const tail = new Promise<void>(resolve => release = resolve);
            this.tails.set(key, tail);

            const run$ = previous ? from(previous).pipe(switchMap(work)) : defer(work);
            return run$.pipe(
                finalize(() => {
                    release();
                    if (this.tails.get(key) === tail) {
                        this.tails.delete(key);
                    }
                }),
            );
        });
    }
}
